/**
 * The one-time offer to save what this browser still holds for the company (2026-09-25). Until then
 * the need, the rates, the wage target and the sales lived in each browser (shell/local.ts); they are
 * the company's on the server now. Nothing is overwritten: a figure the company has already set is
 * not offered, and sales are saved only for days the company has none. Each person is offered only
 * what their role may save. Forkast drops the browser's copy.
 */
import { errorText, get, put } from '@/api/client';
import { ratesTo, useLabour } from '@/api/labour';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { type Sales, readLocal, readNeed, readRates, readSales, removeLocal } from '@/shell/local';
import { useTenant } from '@/shell/tenant';
import { Button, toast } from '@/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

const some = (o: object) => Object.keys(o).length > 0;

export function LocalFiguresOffer() {
  const { tenant, me } = useTenant();
  const can = useCan();
  const labour = useLabour();
  const qc = useQueryClient();
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  const id = tenant?.id;
  if (!id || !labour.ready || gone) return null;

  const s = labour.settings;
  const need = readNeed(id);
  const rates = readRates(id);
  const target = readLocal<unknown>('wage-target', id, null);
  const sales: Sales = readSales(id);
  const offer = {
    need: can('innstillinger.endre') && some(need) && !some(s?.staffing_need ?? {}),
    rates:
      me?.is_admin === true &&
      some(rates) &&
      Boolean(s?.rates) &&
      !Object.values(s?.rates ?? {}).some((f) => f.set),
    target:
      can('vaktplan.kost') &&
      typeof target === 'number' &&
      Boolean(s?.wage_target_pct) &&
      !s?.wage_target_pct?.set,
    sales: can('vaktplan.kost') && some(sales),
  };
  const names = [
    offer.need && T('bemanningsbehovet'),
    offer.rates && T('arbeidsgiverkostnadene'),
    offer.target && T('lønnsmålet'),
    offer.sales && `${T('omsetningen for')} ${Object.keys(sales).length} ${T('dager')}`,
  ].filter(Boolean);
  if (!names.length) return null;

  const forget = () => {
    if (offer.need) removeLocal('need', id);
    if (offer.rates) removeLocal('rates', id);
    if (offer.target) removeLocal('wage-target', id);
    if (offer.sales) removeLocal('sales', id);
    setGone(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      const patch: Record<string, unknown> = {};
      if (offer.need) patch.staffing_need = need;
      if (offer.rates) Object.assign(patch, ratesTo(rates));
      if (offer.target) patch.wage_target_pct = target;
      if (Object.keys(patch).length)
        await put('/admin/labour-settings', { revision: labour.revision, ...patch });
      if (offer.sales) {
        // only the days the company has none for, so another manager's figures are never replaced
        const days = Object.keys(sales).sort();
        const have = await get<{ days: Sales }>(`/admin/sales?from=${days[0]}&to=${days[days.length - 1]}`);
        const missing = Object.fromEntries(
          days.filter((d) => have.days[d] === undefined).map((d) => [d, sales[d]]),
        );
        const keys = Object.keys(missing);
        for (let i = 0; i < keys.length; i += 400)
          await put('/admin/sales', {
            days: Object.fromEntries(keys.slice(i, i + 400).map((d) => [d, missing[d]])),
          });
      }
      await qc.invalidateQueries({ queryKey: ['labour-settings', id] });
      await qc.invalidateQueries({ queryKey: ['sales', id] });
      toast(T('Lagret for bedriften'));
      forget();
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="al info" data-testid="local-figures-offer">
      <span>ⓘ</span>
      <span>
        {T('Denne nettleseren har tall som ikke er lagret for bedriften')}: {names.join(', ')}.{' '}
        {T('Nå lagres de for hele bedriften, så alle ser de samme tallene.')}{' '}
        <Button small disabled={busy} onClick={save}>
          {T('Lagre for bedriften')}
        </Button>{' '}
        <Button small ghost disabled={busy} onClick={forget}>
          {T('Forkast')}
        </Button>
      </span>
    </div>
  );
}
